/**
 * MappingSuggestions.js
 *
 * Deterministic (no backend / no AI) engine that proposes and validates rate mappings
 * between local rates/rooms and a channel's rates/rooms.
 *
 * Pure functions only (no React, no API calls) so they can be unit tested and, later,
 * fed into an LLM step for the ambiguous (medium/low confidence) cases.
 *
 * Data shapes (as produced by ChannelConf.js):
 *  - localRooms: [{ id, code, description, rateList: [{ id, code, description, occupancyList:[{adults,childrens,id,position}], mappingList:[...] }] }]
 *  - channelDataMap: [{ id, roomCode, roomDescription, rateCode, rateDescription, occupancies, active, mappingNotNeeded, hotelBedsSpecific, extraMapping }]
 *
 * Scope (MVP): "simple" channels keyed by (roomCode, rateCode). Channels that need an
 * availabilityGroupMapping (HotelBeds / meal-plan / Airbnb) are intentionally out of scope
 * here — see channelSupportsSuggestions().
 */

// High-resolution clock when available (browser/Node), 0 otherwise — used only for timing logs.
const perfNow = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : 0);

// Confidence tiers on the final 0..1 score.
export const SUGGESTION_TIERS = { high: 0.8, medium: 0.5 };

export function getTier(score) {
    if (score >= SUGGESTION_TIERS.high) return 'high';
    if (score >= SUGGESTION_TIERS.medium) return 'medium';
    return 'low';
}

//#region interface-mode rules (single source of truth, also used by EditChannelMapping)

// Interface (inventory) modes — see getInterfaceMode()/generic.inventoryModeN labels.
export const INTERFACE_MODE = {
    INVENTORY: 1,       // availability only (receive)
    RESERVATION: 2,     // receive reservations only
    TWO_WAY: 3,         // price + availability
    TWO_WAY_NO_PRICES: 4,
    PRICE_ONLY: 5,
    RESTRICTION_ONLY: 6
};

// Returns the i18n id of a warning when `mode` is invalid for the channel rate, else null.
// Based on EditChannelMapping.validateInterfaceMode, but fixing a pre-existing bug there: the
// PriceOnly branch used `&&` between the mode checks, which is always false (a mode can't equal
// 1, 3 and 4 at once), so invalid modes 1/3/4 were never caught. It must be `||`.
export function interfaceModeWarning(channelData, mode) {
    if (channelData && channelData.rateType && mode) {
        if (channelData.rateType === 'AvailabilityOnly' && (mode === 3 || mode === 4 || mode === 5 || mode === 6)) {
            return 'EditChannelMapping.InterfaceWarningText';
        }
        else if (channelData.rateType === 'PriceOnly' && (mode === 1 || mode === 3 || mode === 4 || mode === 6)) {
            return 'EditChannelMapping.InterfaceWarningText';
        }
    }
    if (channelData && channelData.isChildRate !== true && mode === 6) {
        return 'ChannelMapping.MismatchInterfaceMode';
    }
    return null;
}

export function isInterfaceModeValid(channelData, mode) {
    return interfaceModeWarning(channelData, mode) === null;
}

// A "dependent" channel rate cannot be a full 2-way mapping (price + availability).
// AvailabilityOnly => can only receive (Inventory/Reservation); child rates are derived.
export function isDependentRate(channelData) {
    return !!channelData && (
        channelData.rateType === 'AvailabilityOnly' ||
        channelData.rateType === 'PriceOnly' ||
        channelData.isChildRate === true
    );
}

// Resolve the interface mode to actually use: keep the user's preferred mode when it is
// valid for this rate, otherwise fall back to a valid one (prefer Reservation, then Inventory).
export function resolveInterfaceMode(channelData, preferred) {
    if (preferred && isInterfaceModeValid(channelData, preferred)) return preferred;
    const order = [INTERFACE_MODE.RESERVATION, INTERFACE_MODE.INVENTORY, INTERFACE_MODE.TWO_WAY_NO_PRICES,
        INTERFACE_MODE.TWO_WAY, INTERFACE_MODE.PRICE_ONLY, INTERFACE_MODE.RESTRICTION_ONLY];
    const valid = order.find(m => isInterfaceModeValid(channelData, m));
    return valid != null ? valid : preferred;
}

//#endregion

// Channels with multi-key mappings (group/meal-plan/contract) are excluded from the MVP.
export function channelSupportsSuggestions(channelDataMap) {
    if (!channelDataMap || channelDataMap.length === 0) return false;
    return !channelDataMap.some(cd => cd.hotelBedsSpecific || cd.extraMapping);
}

//#region string similarity

export function normalize(str) {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '') // strip accents
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')     // punctuation -> space
        .trim();
}

// A reusable string "profile": the normalized form, its token set, and its character-bigram
// multiset. Building a profile is the expensive part of every similarity comparison, so callers
// that compare the same string many times (suggestMappings: every local rate vs every channel
// candidate) build one profile per distinct string and reuse it — turning the O(rates x candidates)
// re-normalizations into O(rates + candidates).
export function buildProfile(str) {
    const norm = normalize(str);
    const noSpace = norm.replace(/ /g, '');
    const bigrams = new Map();
    for (let i = 0; i < noSpace.length - 1; i++) {
        const bg = noSpace.substr(i, 2);
        bigrams.set(bg, (bigrams.get(bg) || 0) + 1);
    }
    return {
        empty: norm.length === 0,
        tokenSet: new Set(norm ? norm.split(' ').filter(Boolean) : []),
        noSpace,
        bigrams,
        bigramCount: noSpace.length > 0 ? noSpace.length - 1 : 0
    };
}

// Jaccard overlap over the token sets (order independent, good for word swaps).
function tokenSetSimProfile(pa, pb) {
    const sa = pa.tokenSet, sb = pb.tokenSet;
    if (sa.size === 0 && sb.size === 0) return 1;
    if (sa.size === 0 || sb.size === 0) return 0;
    let inter = 0;
    sa.forEach(t => { if (sb.has(t)) inter++; });
    return inter / (sa.size + sb.size - inter);
}

// Sørensen–Dice over character bigrams (good for typos / minor spelling diffs). The intersection
// of the two bigram multisets is sum of min(countA, countB); iterate the smaller map for speed.
function diceSimProfile(pa, pb) {
    if (pa.noSpace === pb.noSpace) return 1;
    if (pa.bigramCount < 1 || pb.bigramCount < 1) return 0;
    const [small, large] = pa.bigrams.size <= pb.bigrams.size ? [pa.bigrams, pb.bigrams] : [pb.bigrams, pa.bigrams];
    let inter = 0;
    small.forEach((cnt, bg) => {
        const other = large.get(bg);
        if (other) inter += other < cnt ? other : cnt;
    });
    return (2 * inter) / (pa.bigramCount + pb.bigramCount);
}

// Combined description similarity in [0,1] from two prebuilt profiles.
function textSimilarityProfile(pa, pb) {
    if (pa.empty || pb.empty) return 0;
    return Math.max(tokenSetSimProfile(pa, pb), 0.85 * diceSimProfile(pa, pb));
}

// Combined description similarity in [0,1].
export function textSimilarity(a, b) {
    if (!a || !b) return 0;
    return textSimilarityProfile(buildProfile(a), buildProfile(b));
}

// Code match boost: exact = 1, one is prefix/contained in the other = 0.6.
export function codeSimilarity(a, b) {
    const na = normalize(a).replace(/ /g, '');
    const nb = normalize(b).replace(/ /g, '');
    if (!na || !nb) return 0;
    if (na === nb) return 1;
    if (na.startsWith(nb) || nb.startsWith(na) || na.includes(nb) || nb.includes(na)) return 0.6;
    return 0;
}

//#endregion

//#region occupancy

// Accepts both object occupancies ({adults, childrens}) and "adults-children" strings.
function parseOccupancies(list) {
    if (!list) return [];
    return list.map(o => {
        if (typeof o === 'string') {
            const [adults, childrens] = o.split('-').map(n => parseInt(n, 10) || 0);
            return { adults, childrens };
        }
        return { adults: o.adults || 0, childrens: o.childrens ?? o.children ?? 0 };
    }).map(o => `${o.adults}-${o.childrens}`);
}

// Whether a usable occupancy list is present (some channels never send occupancy).
export function hasOccupancyData(list) {
    return Array.isArray(list) && list.length > 0;
}

// The occupancies a mapping actually pushes. A mapping's `occupationsToSend` is a list of
// occupancy IDs (a subset of the rate's occupancyList); when present it is what really
// reaches the channel, so it — not the full occupancyList — is what should be compared.
export function effectiveOccupancies(rate, mapping) {
    const list = (rate && rate.occupancyList) || [];
    const ids = mapping && mapping.occupationsToSend;
    if (Array.isArray(ids) && ids.length > 0) {
        const idSet = new Set(ids);
        const subset = list.filter(o => o && typeof o === 'object' && idSet.has(o.id));
        if (subset.length > 0) return subset;
    }
    return list;
}

// 1 if the occupancy sets match exactly, otherwise the Jaccard overlap of the sets.
export function occupancySimilarity(localOccList, channelOccList) {
    const la = new Set(parseOccupancies(localOccList));
    const lb = new Set(parseOccupancies(channelOccList));
    return occupancyJaccard(la, lb);
}

// Set of "adults-childrens" keys for an occupancy list, or null when there is no usable data.
// suggestMappings precomputes one per rate and per candidate so the parse isn't repeated for
// every (rate, candidate) pair.
function occupancyKeySet(list) {
    return hasOccupancyData(list) ? new Set(parseOccupancies(list)) : null;
}

// Jaccard overlap of two prebuilt occupancy key sets (shared by occupancySimilarity and the
// precomputed fast path). Empty/unknown -> 0.5 neutral, matching the original behaviour.
function occupancyJaccard(la, lb) {
    if (!la || !lb || la.size === 0 || lb.size === 0) return 0.5;
    let inter = 0;
    la.forEach(o => { if (lb.has(o)) inter++; });
    return inter / (la.size + lb.size - inter);
}

//#endregion

//#region candidates & helpers

const channelKey = cd => `${cd.roomCode}|${cd.rateCode}`;

function buildCandidates(channelDataMap) {
    return (channelDataMap || []).filter(cd => cd.roomCode && cd.rateCode && !cd.mappingNotNeeded);
}

// Keys already consumed by an existing mapping, so we can avoid proposing duplicates.
function usedChannelKeys(localRooms) {
    const used = new Set();
    (localRooms || []).forEach(room => {
        (room.rateList || []).forEach(rate => {
            (rate.mappingList || []).forEach(m => {
                if (m.roomCode && m.rateCode) used.add(`${m.roomCode}|${m.rateCode}`);
            });
        });
    });
    return used;
}

function isRateUnmapped(rate) {
    return !rate.mappingList || rate.mappingList.length === 0 || !rate.mappingList.some(m => m.roomCode);
}

//#endregion

/**
 * Suggest channel candidates for every unmapped local rate.
 *
 * @returns Array<{
 *   roomId, roomCode, roomDescription, rateId, rateCode, rateDescription, occupancyList,
 *   suggestions: Array<{ channelId, channelRoomCode, channelRateCode, channelRoomDescription,
 *                        channelRateDescription, score, tier, reasons:string[] }>
 * }>
 */
export function suggestMappings(localRooms, channelDataMap, options = {}) {
    const t0 = perfNow();
    const { maxSuggestions = 3, floor = 0.15 } = options;
    const candidates = buildCandidates(channelDataMap);
    const used = usedChannelKeys(localRooms);

    // Precompute each candidate's description profiles ONCE. Without this every (rate, candidate)
    // pair re-normalized + re-tokenized the same channel strings — the dominant cost when there
    // are many unmapped rates.
    const candProfiles = candidates.map(cd => ({
        room: buildProfile(cd.roomDescription),
        rate: buildProfile(cd.rateDescription),
        occ: occupancyKeySet(cd.occupancies)
    }));

    const results = [];
    let unmappedCount = 0;

    (localRooms || []).forEach(room => {
        const roomProfile = buildProfile(room.description); // once per room, not per candidate
        (room.rateList || []).forEach(rate => {
            if (!isRateUnmapped(rate)) return;
            unmappedCount++;
            const rateProfile = buildProfile(rate.description); // once per rate, not per candidate
            const rateOcc = occupancyKeySet(rate.occupancyList); // once per rate, not per candidate

            const scored = candidates.map((cd, ci) => {
                const reasons = [];
                const cp = candProfiles[ci];

                const roomScore = Math.max(
                    textSimilarityProfile(roomProfile, cp.room),
                    codeSimilarity(room.code, cd.roomCode)
                );
                const rateScore = Math.max(
                    textSimilarityProfile(rateProfile, cp.rate),
                    codeSimilarity(rate.code, cd.rateCode)
                );

                // Occupancy is only a signal when BOTH sides provide it; otherwise its 0.2
                // weight is redistributed to room/rate so scores aren't capped artificially.
                const hasOcc = rateOcc !== null && cp.occ !== null;
                const occScore = hasOcc ? occupancyJaccard(rateOcc, cp.occ) : null;

                let score = hasOcc
                    ? 0.4 * roomScore + 0.4 * rateScore + 0.2 * occScore
                    : 0.5 * roomScore + 0.5 * rateScore;

                if (roomScore >= 0.8) reasons.push({ type: 'roomMatch' });
                if (rateScore >= 0.8) reasons.push({ type: 'rateMatch' });
                if (hasOcc && occScore >= 0.999) reasons.push({ type: 'occupancyMatch' });
                else if (hasOcc && occScore < 0.5) reasons.push({ type: 'occupancyMismatch' });

                if (used.has(channelKey(cd))) {
                    score *= 0.5;
                    reasons.push({ type: 'alreadyUsed' });
                }
                if (cd.active === false) {
                    score *= 0.85;
                    reasons.push({ type: 'inactiveOnChannel' });
                }

                const dependent = isDependentRate(cd);
                if (dependent) reasons.push({ type: 'receiveOnly' });

                return {
                    channelId: cd.id,
                    channelRoomCode: cd.roomCode,
                    channelRateCode: cd.rateCode,
                    channelRoomDescription: cd.roomDescription,
                    channelRateDescription: cd.rateDescription,
                    rateType: cd.rateType || null,
                    isChildRate: cd.isChildRate === true,
                    dependent,
                    score: Math.round(score * 100) / 100,
                    tier: getTier(score),
                    reasons
                };
            })
            .filter(s => s.score >= floor)
            .sort((a, b) => b.score - a.score)
            .slice(0, maxSuggestions);

            results.push({
                roomId: room.id,
                roomCode: room.code,
                roomDescription: room.description,
                rateId: rate.id,
                rateCode: rate.code,
                rateDescription: rate.description,
                occupancyList: rate.occupancyList,
                suggestions: scored
            });
        });
    });

    // One-line timing so the cost is observable in the browser console (DevTools → Verbose).
    if (typeof console !== 'undefined' && console.debug) {
        console.debug(`[MappingSuggestions] suggestMappings: ${unmappedCount} unmapped rates x ${candidates.length} candidates = ${unmappedCount * candidates.length} comparisons in ${(perfNow() - t0).toFixed(1)}ms`);
    }

    return results;
}

/**
 * Health-check of EXISTING mappings: flags likely-wrong mappings.
 *
 * @returns Array<{ roomId, rateId, mappingId, roomCode, rateCode, channelRoomCode,
 *                  channelRateCode, severity:'error'|'warning', issues:Array<{type, values?}> }>
 */
export function validateExistingMappings(localRooms, channelDataMap, options = {}) {
    // occupancyMismatchThreshold: occupancy Jaccard below this flags an occupancyMismatch. Kept
    // well below 1 so near-identical profiles (e.g. 0.95) are not flagged as problems; only a
    // genuine divergence is. occCorroborateThreshold stays near-exact: corroboration of an
    // otherwise text-divergent mapping must be a real match, not a partial overlap.
    const { lowSimilarityThreshold = 0.3, occupancyMismatchThreshold = 0.5,
        occCorroborateThreshold = 0.999 } = options;
    const candidates = buildCandidates(channelDataMap);
    const candidateByKey = new Map(candidates.map(cd => [channelKey(cd), cd]));

    // Count how many local rates point at each channel key (for duplicate detection).
    const keyUsage = new Map();
    (localRooms || []).forEach(room => {
        (room.rateList || []).forEach(rate => {
            (rate.mappingList || []).forEach(m => {
                if (m.roomCode && m.rateCode) {
                    const k = `${m.roomCode}|${m.rateCode}`;
                    keyUsage.set(k, (keyUsage.get(k) || 0) + 1);
                }
            });
        });
    });

    const problems = [];

    (localRooms || []).forEach(room => {
        (room.rateList || []).forEach(rate => {
            (rate.mappingList || []).forEach(m => {
                if (!m.roomCode || !m.rateCode) return;

                const key = `${m.roomCode}|${m.rateCode}`;
                const cd = candidateByKey.get(key);
                const issues = [];
                let severity = 'warning';

                if (!cd) {
                    issues.push({ type: 'codeNotFound' });
                    severity = 'error';
                } else {
                    const rateSim = textSimilarity(rate.description, cd.rateDescription);
                    const roomSim = textSimilarity(room.description, cd.roomDescription);

                    // Compare the occupancies actually sent (occupationsToSend) — not the full
                    // rate list — against the channel, and only when both sides provide them.
                    const occList = effectiveOccupancies(rate, m);
                    const occSim = (hasOccupancyData(occList) && hasOccupancyData(cd.occupancies))
                        ? occupancySimilarity(occList, cd.occupancies) : null;
                    // Corroboration only counts when the occupancy profiles match AND are
                    // non-trivial (>=3 distinct occupancies); a lone shared occupancy like
                    // "2-0" is too common to vouch for an otherwise text-divergent mapping.
                    const occCorroborates = occSim !== null && occSim >= occCorroborateThreshold
                        && new Set(parseOccupancies(occList)).size >= 3;

                    // Low text similarity is a weak signal across languages (e.g. PT room name
                    // vs EN channel name); suppress it when occupancy strongly corroborates.
                    if (rateSim < lowSimilarityThreshold && roomSim < lowSimilarityThreshold
                        && !occCorroborates) {
                        issues.push({ type: 'lowSimilarity', values: { roomSim, rateSim } });
                    }
                    if (occSim !== null && occSim < occupancyMismatchThreshold) {
                        issues.push({ type: 'occupancyMismatch' });
                    }
                    // Dependent rate (e.g. AvailabilityOnly) mapped with a price-sending mode.
                    if (m.interfaceMode && !isInterfaceModeValid(cd, m.interfaceMode)) {
                        issues.push({ type: 'invalidInterfaceMode' });
                    }
                    if (cd.active === false) {
                        issues.push({ type: 'inactiveOnChannel' });
                    }
                }

                if ((keyUsage.get(key) || 0) > 1) {
                    issues.push({ type: 'duplicate', values: { count: keyUsage.get(key) } });
                }

                if (issues.length > 0) {
                    problems.push({
                        roomId: room.id,
                        rateId: rate.id,
                        mappingId: m.id,
                        roomCode: m.roomCode,
                        rateCode: m.rateCode,
                        channelRoomCode: m.roomCode,
                        channelRateCode: m.rateCode,
                        localRoomDescription: room.description,
                        localRateDescription: rate.description,
                        severity,
                        issues
                    });
                }
            });
        });
    });

    return problems;
}
